import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // Send cookies
});

// Add auth token from cookie OR header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vx_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('vx_session_token');
      localStorage.removeItem('vx_user');
      // Don't redirect if already on login, welcome, or processing auth
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/welcome') &&
          !window.location.hash?.includes('session_id=')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  session: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  login: (data) => api.post('/auth/login', data),
  testLogin: (data) => api.post('/auth/test-login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Branches
export const branchAPI = {
  list: () => api.get('/branches'),
  listAll: () => api.get('/branches/all'),
  get: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
  todaySchedule: (id) => api.get(`/branches/today-schedule/${id}`),
};

// Employees
export const employeeAPI = {
  list: (branchId) => api.get('/employees', { params: { branch_id: branchId } }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  permanentDelete: (id) => api.delete(`/employees/${id}/permanent`),
  setPassword: (id, password) => api.put(`/employees/${id}/set-password`, { password }),
  offboard: (id, data) => api.post(`/employees/${id}/offboard`, data),
};

// Attendance
export const attendanceAPI = {
  checkIn: (data) => api.post('/attendance/checkin', data),
  checkOut: (data) => api.post('/attendance/checkout', data),
  myStatus: () => api.get('/attendance/my-status'),
  today: (branchId) => api.get(`/attendance/today/${branchId}`),
  monthly: (params) => api.get('/attendance/monthly', { params }),
};

// Admin - Manual Data Entry
export const adminAPI = {
  createManualAttendance: (data) => api.post('/admin/manual-attendance', data),
  listManualAttendance: (params) => api.get('/admin/manual-attendance', { params }),
  deleteManualAttendance: (id) => api.delete(`/admin/manual-attendance/${id}`),
  createManualLeave: (data) => api.post('/admin/manual-leave', data),
  listManualLeaves: (params) => api.get('/admin/manual-leaves', { params }),
  deleteManualLeave: (id) => api.delete(`/admin/manual-leave/${id}`),
  dailyOverview: (branchId, date) => api.get('/admin/daily-overview', { params: { branch_id: branchId, date } }),
  fixAttendance: (data) => api.put('/admin/fix-attendance', data),
};

// Leave Types
export const leaveTypeAPI = {
  list: (branchId) => api.get('/leave-types', { params: { branch_id: branchId } }),
  create: (data) => api.post('/leave-types', data),
  update: (id, data) => api.put(`/leave-types/${id}`, data),
  delete: (id) => api.delete(`/leave-types/${id}`),
};

// Holidays
export const holidayAPI = {
  list: (branchId, year) => api.get('/holidays', { params: { branch_id: branchId, year } }),
  create: (data) => api.post('/holidays', data),
  delete: (id) => api.delete(`/holidays/${id}`),
};

// Leave Requests
export const leaveRequestAPI = {
  list: (params) => api.get('/leave-requests', { params }),
  create: (data) => api.post('/leave-requests', data),
  managerAction: (id, data) => api.put(`/leave-requests/${id}/manager-action`, data),
  superUserAction: (id, data) => api.put(`/leave-requests/${id}/super-user-action`, data),
  balance: (employeeId) => api.get(`/leave-balance/${employeeId}`),
};

// Salary
export const salaryAPI = {
  calculate: (data) => api.post('/salary/calculate', data),
  report: (branchId, year, month) => api.get('/salary/report', { params: { branch_id: branchId, year, month } }),
  detail: (recordId) => api.get(`/salary/detail/${recordId}`),
  deleteRun: (branchId, year, month) => api.delete('/salary/run', { params: { branch_id: branchId, year, month } }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

// Tasks & Daily Work Logs
export const taskAPI = {
  list: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  addLog: (id, data) => api.post(`/tasks/${id}/logs`, data),
  activity: (params) => api.get('/tasks/activity', { params }),
  startTimer: (id) => api.post(`/tasks/${id}/timer/start`),
  stopTimer: (id) => api.post(`/tasks/${id}/timer/stop`),
};

// Notifications
export const notificationAPI = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Leads / CRM
export const leadAPI = {
  list: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.put(`/leads/${id}`, data),
  addNote: (id, data) => api.post(`/leads/${id}/notes`, data),
  createTask: (id, data) => api.post(`/leads/${id}/create-task`, data),
  stats: () => api.get('/leads/stats'),
  analytics: () => api.get('/leads/analytics'),
};

// App settings (super user)
export const settingsAPI = {
  getMarketingKey: () => api.get('/settings/marketing-key'),
  regenerateMarketingKey: () => api.post('/settings/marketing-key/regenerate'),
  setMarketingKey: (value) => api.put('/settings/marketing-key', { value }),
  testMarketingKey: (key) => api.post('/integrations/marketing-leads', {
    external_id: `__conntest__${Date.now()}`,
    name: 'Connection Test',
    email: 'connection.test@visaxpress.local',
    phone: '+10000000000',
    source: 'settings-test',
  }, { headers: { 'X-API-Key': key } }),
  cleanupTestLeads: () => api.post('/settings/marketing-test-cleanup'),
};

// Devices (after-hours work-laptop binding)
export const deviceAPI = {
  me: (deviceId) => api.get('/devices/me', { params: { device_id: deviceId } }),
  register: (deviceId, label) => api.post('/devices/register', { device_id: deviceId, label }),
  list: (status) => api.get('/devices', { params: status ? { status } : {} }),
  approve: (id) => api.post(`/devices/${id}/approve`),
  revoke: (id) => api.post(`/devices/${id}/revoke`),
};

// After-hours / overtime time log
export const overtimeAPI = {
  active: (deviceId) => api.get('/overtime/active', { params: { device_id: deviceId } }),
  start: (payload) => api.post('/overtime/start', payload),
  stop: (deviceId) => api.post('/overtime/stop', { device_id: deviceId }),
  manual: (entries) => api.post('/overtime/manual', { entries }),
  mine: () => api.get('/overtime/mine'),
  list: (status) => api.get('/overtime', { params: status ? { status } : {} }),
  approve: (id) => api.post(`/overtime/${id}/approve`),
  reject: (id, note) => api.post(`/overtime/${id}/reject`, { note }),
  report: () => api.get('/overtime/report'),
};

export default api;
